import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { ContactsFilters, type ContactsFiltersValue } from '../components/contacts/ContactsFilters';
import { TagBadge } from '../components/contacts/TagBadge';
import {
  LABEL_STATUT_PIPELINE,
  LABEL_TERRITOIRE_CONTACT,
  LABEL_TYPE_CLIENT,
  formatDate,
} from '../lib/format';

const EMPTY: ContactsFiltersValue = {
  q: '',
  territoire: '',
  type_client: '',
  statut_pipeline: '',
  source: '',
  departement: '',
  tag: '',
  sort: 'nom_asc',
};

export function ContactsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: ContactsFiltersValue = useMemo(
    () => ({
      q: searchParams.get('q') ?? '',
      territoire: searchParams.get('territoire') ?? '',
      type_client: searchParams.get('type_client') ?? '',
      statut_pipeline: searchParams.get('statut_pipeline') ?? '',
      source: searchParams.get('source') ?? '',
      departement: searchParams.get('departement') ?? '',
      tag: searchParams.get('tag') ?? '',
      sort: searchParams.get('sort') ?? 'nom_asc',
    }),
    [searchParams],
  );

  const page = Number.parseInt(searchParams.get('page') ?? '1', 10);

  const tagsQuery = useQuery({
    queryKey: ['contacts', 'tags'],
    queryFn: () => api.contacts.tags(),
    staleTime: 60_000,
  });

  const query = useQuery({
    queryKey: ['contacts', 'list', filters, page],
    queryFn: () =>
      api.contacts.list({
        q: filters.q === '' ? undefined : filters.q,
        territoire: filters.territoire === '' ? undefined : filters.territoire,
        type_client: filters.type_client === '' ? undefined : filters.type_client,
        statut_pipeline: filters.statut_pipeline === '' ? undefined : filters.statut_pipeline,
        source: filters.source === '' ? undefined : filters.source,
        departement: filters.departement === '' ? undefined : filters.departement,
        tag: filters.tag === '' ? undefined : filters.tag,
        sort: filters.sort as 'nom_asc',
        page,
        page_size: 25,
      }),
  });

  const updateFilters = (v: ContactsFiltersValue): void => {
    const next = new URLSearchParams();
    for (const [k, val] of Object.entries(v)) {
      if (val !== '' && !(k === 'sort' && val === 'nom_asc')) next.set(k, val);
    }
    setSearchParams(next);
  };

  const total = query.data?.total ?? 0;
  const pageSize = query.data?.page_size ?? 25;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const goToPage = (p: number): void => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
  };

  return (
    <div className="max-w-7xl">
      <header className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Contacts</h1>
          <p className="text-xs text-slate-500 mt-1">Clients et prospects.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/contacts/import">
            <Button variant="secondary">Importer CSV</Button>
          </Link>
          <Link to="/contacts/nouveau">
            <Button>+ Nouveau contact</Button>
          </Link>
        </div>
      </header>

      <div className="mb-4">
        <ContactsFilters
          value={filters}
          tags={tagsQuery.data?.tags ?? []}
          onChange={updateFilters}
          onReset={(): void => updateFilters(EMPTY)}
        />
      </div>

      {query.isLoading && <div className="text-sm text-slate-400">Chargement…</div>}
      {query.isError && <div className="text-sm text-red-400">Erreur de chargement.</div>}

      {query.isSuccess && query.data.items.length === 0 && (
        <EmptyState
          title="Aucun contact sur ces critères."
          description="Ajustez les filtres ou créez-en un."
          action={
            <Link to="/contacts/nouveau">
              <Button>+ Nouveau contact</Button>
            </Link>
          }
        />
      )}

      {query.isSuccess && query.data.items.length > 0 && (
        <>
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-xs text-slate-400 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left">Contact</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Statut</th>
                  <th className="px-4 py-2 text-left">Territoire</th>
                  <th className="px-4 py-2 text-left">Dépt</th>
                  <th className="px-4 py-2 text-left">Tags</th>
                  <th className="px-4 py-2 text-left">Dernière interaction</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {query.data.items.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-900/60">
                    <td className="px-4 py-2">
                      <Link
                        to={`/contacts/${String(c.id)}`}
                        className="text-slate-100 hover:text-floryon-green"
                      >
                        <div className="font-medium">
                          {c.societe ?? `${c.prenom ?? ''} ${c.nom}`.trim()}
                        </div>
                        {c.societe !== null && (
                          <div className="text-xs text-slate-500">
                            {`${c.prenom ?? ''} ${c.nom}`.trim()}
                          </div>
                        )}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-slate-300 text-xs">
                      {LABEL_TYPE_CLIENT[c.type_client] ?? c.type_client}
                    </td>
                    <td className="px-4 py-2 text-slate-300 text-xs">
                      {LABEL_STATUT_PIPELINE[c.statut_pipeline] ?? c.statut_pipeline}
                    </td>
                    <td className="px-4 py-2 text-slate-300 text-xs">
                      {LABEL_TERRITOIRE_CONTACT[c.territoire] ?? c.territoire}
                    </td>
                    <td className="px-4 py-2 text-slate-300 text-xs">{c.departement ?? '—'}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {c.tags.map((t) => (
                          <TagBadge key={t} label={t} />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-slate-400 text-xs">
                      {formatDate(c.date_derniere_interaction)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
            <div>
              {total} contact{total > 1 ? 's' : ''} — page {page} / {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={(): void => goToPage(page - 1)}
              >
                Précédent
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={(): void => goToPage(page + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
